import type { WellProperties } from "@/composables/forms/formTypes";

import { Water_Viscosity_Options_Enum } from "gql/api";
import { CubicFeetPerSecond } from "@/libs/units/flowRate";
import { Inches } from "../units/length";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import { evaluateLagrangePolynomialWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import { GRAVITY_FT_SEC } from "./constants";

// temp T F
// press P psia
// water spec grav S_water SpG
// water viscosity correlation
// returns mu_w cP
export function get_water_phase_in_situ_viscosity(
  tempf: number,
  pressure: number,
  water_gravity: number,
  corr_water_viscosity: Water_Viscosity_Options_Enum | undefined,
  salinity_ppm = 0,
  customViscosityProperties: LagrangeInterpolationMapWithGroupings | null
): number {
  if (customViscosityProperties?.waterViscosityCP != null) {
    return evaluateLagrangePolynomialWithGroupings(
      customViscosityProperties.waterViscosityCP,
      pressure,
      tempf
    );
  }

  if (corr_water_viscosity === Water_Viscosity_Options_Enum.VanWingen) {
    return Math.exp(1.003 - 0.01479 * tempf + 0.00001982 * tempf ** 2);
  } else if (
    corr_water_viscosity === Water_Viscosity_Options_Enum.MatthewsRussel
  ) {
    const salinityRatio = salinity_ppm / 1000000;
    return (
      (38.3 / Math.pow(tempf, 0.5) - 14.6 / Math.pow(tempf, 0.25) + 1.48) *
      (1 + salinityRatio)
    );
  } else if (corr_water_viscosity === Water_Viscosity_Options_Enum.Mccain) {
    const Cw = salinity_ppm / water_gravity / 10000;
    const A =
      109.574 - 8.40564 * Cw + 0.313314 * Cw ** 2 + 0.00872213 * Cw ** 3;
    const B =
      1.12166 -
      2.63951e-2 * Cw +
      6.79461e-4 * Cw ** 2 +
      5.47119e-5 * Cw ** 3 -
      1.55586e-6 * Cw ** 4;
    const u_w1 = A * Math.pow(tempf, -B);
    return u_w1 * (0.9994 + 4.0295e-5 * pressure + 3.1062e-9 * pressure ** 2);
  }

  return 0;
}

// /static/Al-Hassan.Nat.sep.xlsx
export function calculateBubbleRiseVerticalVelocity(
  liquidDensityLBMFT3: number,
  gasDensityLBMFT3: number
): number {
  return Math.sqrt(
    2 *
      Math.pow(
        (1.28 * (liquidDensityLBMFT3 - gasDensityLBMFT3)) /
          (liquidDensityLBMFT3 - gasDensityLBMFT3),
        0.25
      )
  );
}

// /static/Al-Hassan.Nat.sep.xlsx
export function calculateBubbleRiseRadialVelocity(
  liquidDensityLBMFT3: number,
  gasDensityLBMFT3: number,
  liquidSurfaceTensionDYCM: number
): number {
  return Math.sqrt(
    2 *
      Math.pow(
        (liquidSurfaceTensionDYCM *
          (liquidDensityLBMFT3 - gasDensityLBMFT3) *
          GRAVITY_FT_SEC) /
          liquidDensityLBMFT3 ** 2,
        0.25
      )
  );
}

// gas flow rate q_g_corr cf/sec
// tubing ID T_id inches
// GOR scf/stb
// gas solub oil R_so scf/stb
// gas solub water R_sw_corr scf/stb
// returns V_sg_corr ft/sec
export function getGasSuperficialVelocityFtSec(
  gasFlowRateBbld: number,
  oilFlowRateBBLD: number,
  tubingInternalDiameter: number,
  GOR: number,
  GLR: number,
  oilPhaseGasSolubility: number,
  waterPhaseGasSolubility: number,
  equipmentCrossSectionFt2: number
): number {
  const gasRatio = oilFlowRateBBLD > 0 ? GOR : GLR;
  if (oilPhaseGasSolubility + waterPhaseGasSolubility > gasRatio) {
    return 0;
  } else {
    const diaFt = Inches.toStandardUnit(tubingInternalDiameter);
    let area = (Math.PI / 4) * Math.pow(diaFt, 2);
    if (!area) {
      return 0;
    }
    if (equipmentCrossSectionFt2 > 0) {
      area = equipmentCrossSectionFt2;
    }
    const gasSupVelFtSec = Math.max(
      CubicFeetPerSecond.toThisUnit(gasFlowRateBbld) / area,
      0
    );
    return gasSupVelFtSec;
  }
}

// liquid flow rate q_l_corr cf/sec
// tubing ID T_id inches
// returns V_sl_corr ft/sec
export function getLiquidSuperficialVelocityFtSec(
  flowRateStbd: number,
  tubingInternalDiameter: number,
  equipmentCrossSectionFt2: number
): number {
  const flowRateCfSec = CubicFeetPerSecond.toThisUnit(flowRateStbd);
  const diaFt = Inches.toStandardUnit(tubingInternalDiameter);
  let area = (Math.PI / 4) * Math.pow(diaFt, 2);

  if (equipmentCrossSectionFt2 > 0) {
    area = equipmentCrossSectionFt2;
  }

  if (!area) {
    return 0;
  }

  return flowRateCfSec / area || 0;
}

export function getEquipmentCrossSectionFt2(
  outerTubingInteriorDiameterIn: number,
  equipmentOuterDiameterIn: number
): number {
  const PI = Math.PI;
  const outerRadiusFt = Inches.toStandardUnit(
    outerTubingInteriorDiameterIn / 2
  );
  const equipmentRadiusFt = Inches.toStandardUnit(equipmentOuterDiameterIn / 2);
  const outerAreaFt2 = PI * outerRadiusFt ** 2;
  const equipmentAreaFt2 = PI * equipmentRadiusFt ** 2;
  return outerAreaFt2 - equipmentAreaFt2;
}

// liquid super vel corr V_sl_corr ft/sec
// gas super vel corr V_sg_corr ft/sec
// returns no-slip liquid fraction V_m
export function get_mix_superficial_velocity(
  liquid_superficial_velocity: number,
  gas_superficial_velocity: number
): number {
  return liquid_superficial_velocity + gas_superficial_velocity;
}

// oil phase density rho_o lbm/ft^3
// water phase density rho_w lbm/ft^3
// water cut adjusted WC_a %
// condensate phase density rho_c lbm/ft^3
// condensate fraction of the liquid rate f_c
// returns rho_l lbm/ft^3
export function get_liquid_density(
  oil_density: number,
  water_density: number,
  adjusted_water_cut: number,
  condensate_density = 0,
  condensate_ratio = 0
): number {
  const oil_water_density =
    oil_density * (1 - adjusted_water_cut) + water_density * adjusted_water_cut;
  return (
    oil_water_density * (1 - condensate_ratio) +
    condensate_density * condensate_ratio
  );
}

// oil visc mu_o cP
// water visc mu_w cP
// adjusted water cut WC_a %
// returns mu_l cP
export function get_liquid_viscosity(
  oil_viscosity: number,
  water_viscosity: number,
  water_cut_ratio: number
): number {
  return (
    oil_viscosity * (1 - water_cut_ratio) + water_viscosity * water_cut_ratio
  );
}

export function getFluidDensityLBMFT3(
  liquidDensityLBMFT3: number,
  gasDensityLBMFT3: number,
  gasFlowVolumeRatio: number
): number {
  const fdens =
    liquidDensityLBMFT3 * (1 - gasFlowVolumeRatio) +
    gasDensityLBMFT3 * gasFlowVolumeRatio;
  return fdens;
}

export function turpin_coefficient(
  gas_rate: number,
  liquid_rate: number,
  pump_intake_pressure_psia: number
): number {
  return (2000 * (gas_rate / liquid_rate)) / (3 * pump_intake_pressure_psia);
}

export const getTemperatureForMeasuredDepthBelowPumpF = (
  bottomholeTemperatureF: number,
  surfaceTemperatureF: number,
  datumPoint: number,
  completePumpDepth: number
): number => {
  const tempDiff = bottomholeTemperatureF - surfaceTemperatureF;
  const lengthDiff = datumPoint - completePumpDepth;

  const gradient = tempDiff / datumPoint;
  const change = gradient * lengthDiff;
  return bottomholeTemperatureF - change;
};

export const wellFlowingTemperatureForMD = (
  measuredDepthFt: number,
  datumPointFt: number,
  bottomholeTemperatureF: number,
  surfaceTemperatureF: number
): number => {
  let temp;

  if (datumPointFt === measuredDepthFt) {
    temp = bottomholeTemperatureF;
  } else {
    temp = getTemperatureForMeasuredDepthBelowPumpF(
      bottomholeTemperatureF,
      surfaceTemperatureF,
      datumPointFt,
      measuredDepthFt
    );
  }
  return temp;
};

export const getCasingInteriorDiameter = (
  wellProperties: WellProperties,
  measuredDepthFT: number
): number => {
  let casingDiameterIn =
    wellProperties?.r_top_casing?.interior_diameter_inches || 0;
  // If we have a bottom casing and length for it, use it if segmentLength falls after top casing length
  if (
    wellProperties?.bottom_casing_id &&
    wellProperties?.bottom_casing_length_feet
  ) {
    if (wellProperties?.top_casing_length_feet < measuredDepthFT) {
      casingDiameterIn =
        wellProperties?.r_bottom_casing?.interior_diameter_inches;
    }
  }
  return casingDiameterIn;
};

export const getTubingOuterDiameter = (
  wellProperties: WellProperties,
  measuredDepthFT: number
): number => {
  let tubingDiameterIn = wellProperties?.r_top_tubing?.outer_diameter_inches;
  // If we have a bottom tubing and length for it, use it if segmentLength falls after top tubing length
  if (
    wellProperties?.bottom_tubing_id &&
    wellProperties?.bottom_tubing_length_feet
  ) {
    if (wellProperties?.top_tubing_length_feet < measuredDepthFT) {
      tubingDiameterIn = wellProperties?.r_bottom_tubing?.outer_diameter_inches;
    }
  }
  return tubingDiameterIn;
};
